import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  CreditCard, 
  Building2, 
  Users, 
  DollarSign,
  Activity,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, subValue, color = '#00E87A', loading }) => (
  <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-sm mb-1">{label}</p>
        {loading ? (
          <div className="h-8 w-20 bg-gray-800 rounded animate-pulse" />
        ) : (
          <p className="text-3xl font-bold text-white">{value}</p>
        )}
        {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
      </div>
      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
    </div>
  </div>
);

const SuperAdminOverview = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingPayments: 0,
    totalRevenue: 0,
    activeCompanies: 0,
    totalUsers: 0,
    schedulerStatus: 'unknown'
  });
  const [loading, setLoading] = useState(true);
  const [serviceAlerts, setServiceAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        // Use admin endpoints for super_admin access
        const [ordersRes, companiesRes, usersRes, schedulerRes] = await Promise.all([
          axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { orders: [] } })),
          axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { companies: [] } })),
          axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { users: [] } })),
          axios.get(`${API}/scheduler/health`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { status: 'unknown' } }))
        ]);

        const orders = ordersRes.data.orders || [];
        const companies = companiesRes.data.companies || [];
        const users = usersRes.data.users || [];
        
        // Calculate pending bank transfer payments
        const pendingPayments = orders.filter(
          o => o.payment_method === 'bank_transfer' && o.status === 'payment_pending'
        ).length;

        // Calculate total revenue from confirmed payments
        const totalRevenue = orders
          .filter(o => ['approved', 'completed', 'in_progress', 'submitted'].includes(o.status))
          .reduce((sum, o) => sum + (o.final_amount || o.total_amount || 0), 0);

        setStats({
          totalOrders: orders.length,
          pendingPayments,
          totalRevenue,
          activeCompanies: companies.length,
          totalUsers: users.length,
          schedulerStatus: schedulerRes.data.status || 'unknown'
        });
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    
    // Fetch service alerts
    const fetchAlerts = async () => {
      setAlertsLoading(true);
      try {
        const res = await axios.get(`${API}/admin/dashboard/service-alerts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setServiceAlerts(res.data || []);
      } catch (err) {
        console.error('Error fetching service alerts:', err);
      } finally {
        setAlertsLoading(false);
      }
    };
    fetchAlerts();
  }, [token]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <SuperAdminLayout>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Dashboard Overview</h1>
        <p className="text-gray-400 text-sm mt-1">Monitor platform activity and key metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          icon={FileText}
          label="Total Orders"
          value={stats.totalOrders}
          loading={loading}
        />
        <StatCard
          icon={CreditCard}
          label="Pending Payments"
          value={stats.pendingPayments}
          subValue="Bank transfers awaiting confirmation"
          color="#F59E0B"
          loading={loading}
        />
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={formatCurrency(stats.totalRevenue)}
          subValue="From confirmed payments"
          color="#10B981"
          loading={loading}
        />
        <StatCard
          icon={Building2}
          label="Active Companies"
          value={stats.activeCompanies}
          loading={loading}
        />
        <StatCard
          icon={Users}
          label="Total Users"
          value={stats.totalUsers}
          loading={loading}
        />
        <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-gray-400 text-sm mb-1">Scheduler Status</p>
              {loading ? (
                <div className="h-8 w-20 bg-gray-800 rounded animate-pulse" />
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  {stats.schedulerStatus === 'healthy' ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-[#00E87A]" />
                      <span className="text-[#00E87A] font-medium">Healthy</span>
                    </>
                  ) : stats.schedulerStatus === 'error' ? (
                    <>
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400 font-medium">Error</span>
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                      <span className="text-gray-400 font-medium">Unknown</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-cyan-500/20">
              <Activity className="w-6 h-6 text-cyan-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Service Alerts Widget */}
      <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6 mb-8" data-testid="service-alerts-widget">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          Service Alerts
          {!alertsLoading && serviceAlerts.length > 0 && (
            <span className="text-xs text-gray-500 font-normal ml-1">({serviceAlerts.length})</span>
          )}
        </h2>

        {alertsLoading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div>
        ) : serviceAlerts.length === 0 ? (
          <div className="flex items-center gap-2 py-4 text-green-400" data-testid="no-service-alerts">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">No tracked services yet</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="service-alerts-table">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Company</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Service</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Status</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Expires</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Days Left</th>
                  <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {serviceAlerts.map((alert, idx) => {
                  const urgencyStyle = {
                    expired: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Expired' },
                    expiring_soon: { bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'Expiring Soon' },
                    warning: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'Renewal Due' },
                    active: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Active' },
                    pending_completion: { bg: 'bg-gray-500/15', text: 'text-gray-400', label: 'Pending Completion' }
                  }[alert.urgency] || { bg: 'bg-gray-500/15', text: 'text-gray-400', label: alert.status };

                  return (
                    <tr
                      key={`${alert.company_id}-${alert.service}-${idx}`}
                      className="hover:bg-gray-800/20"
                      data-testid={`alert-row-${alert.company_id}-${alert.service}`}
                    >
                      <td className="px-3 py-2.5 text-white text-sm font-medium">{alert.company_name}</td>
                      <td className="px-3 py-2.5 text-gray-400 text-sm">
                        {alert.service === 'registered_agent' ? 'Reg. Agent' : 'Mail Fwd'}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${urgencyStyle.bg} ${urgencyStyle.text}`}
                          data-testid={`alert-status-${alert.urgency}`}
                        >
                          {urgencyStyle.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-400 text-sm">
                        {alert.due_date
                          ? new Date(alert.due_date + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                          : <span className="text-gray-600 italic">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-sm">
                        {alert.days_left === null || alert.days_left === undefined ? (
                          <span className="text-gray-600">—</span>
                        ) : alert.days_left < 0 ? (
                          <span className="text-red-400 font-medium">{Math.abs(alert.days_left)}d overdue</span>
                        ) : (
                          <span className={alert.days_left <= 30 ? 'text-amber-400 font-medium' : alert.days_left <= 90 ? 'text-yellow-400' : 'text-gray-400'}>
                            {alert.days_left}d
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Link
                          to={`/superadmin/companies/${alert.company_id}`}
                          className="text-[#00E87A] text-xs font-medium hover:underline"
                          data-testid={`alert-edit-${alert.company_id}-${alert.service}`}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/superadmin/payments"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg font-medium text-white transition-colors"
            style={{ backgroundColor: '#F59E0B' }}
            data-testid="view-pending-payments-btn"
          >
            <CreditCard className="w-5 h-5" />
            View Pending Payments
            {stats.pendingPayments > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-sm">
                {stats.pendingPayments}
              </span>
            )}
            <ArrowRight className="w-4 h-4" />
          </Link>
          
          <Link
            to="/superadmin/orders"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg font-medium text-white border border-gray-700 hover:bg-gray-800 transition-colors"
            data-testid="view-all-orders-btn"
          >
            <FileText className="w-5 h-5" />
            View All Orders
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminOverview;
